import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { Challenge, PdfData, TeamReference } from "@/models/pdfData";
import {
  getFromLocalStorage,
  putToLocalStorage,
} from "@/services/localStorageService";
import { sortChallenges } from "@/utils/challenge-utils";
import { SamsScope } from "@/models/samsVbl";
import { loadGameData } from "@/services/samsService";

const VERSION = 1;

interface GameDataValues {
  useGame: (samsId: string, scope: SamsScope) => void;
  games: PdfData[];
  game: PdfData | undefined;
  gameLoadingState: "loading" | "loaded" | "error";
  storeGame: (game: PdfData) => void;
  deleteGame: (samsId: string) => void;
  addChallenge: (samsId: string, challenge: Challenge) => void;
  addChallengeCounter: (
    team: TeamReference,
    set: number,
    gameId?: string,
  ) => void;
  deleteChallenge: (challengeId: string) => void;
  restoreChallenge: (challengeId: string) => void;
  updateLocation: (location: string, gameId?: string) => void;
  updateGameNumber: (gameNumber: string, gameId?: string) => void;
  updateScoresheetPdfUrl: (url: string, gameId?: string) => void;
  updateYoutubeId: (youtubeId: string, gameId?: string) => void;
  updateVideoOffset: (offset: number, gameId?: string) => void;
}

const notInitializedYet = (): any => {
  console.error("GameDataContext has not been initialized yet!");
};

const GameDataContext = createContext<GameDataValues>({
  game: undefined,
  gameLoadingState: "loading",
  games: [],
  useGame: notInitializedYet,
  storeGame: notInitializedYet,
  deleteGame: notInitializedYet,
  addChallenge: notInitializedYet,
  addChallengeCounter: notInitializedYet,
  deleteChallenge: notInitializedYet,
  restoreChallenge: notInitializedYet,
  updateGameNumber: notInitializedYet,
  updateLocation: notInitializedYet,
  updateScoresheetPdfUrl: notInitializedYet,
  updateYoutubeId: notInitializedYet,
  updateVideoOffset: notInitializedYet,
});

const modifyChallengeId = (
  games: PdfData[],
  challengeId: string,
  calculateNewChallengeId: (currentId: string) => string,
): PdfData[] => {
  const gameWithChallenge = games.find((game) =>
    game.challenges.some(({ id }) => id === challengeId),
  );
  const challengeToUpdate = gameWithChallenge?.challenges?.find(
    ({ id }) => id === challengeId,
  );
  if (challengeToUpdate) {
    return [
      ...games.filter((game) => game.samsId !== gameWithChallenge?.samsId),
      ...(gameWithChallenge
        ? [
            {
              ...gameWithChallenge,
              challenges: sortChallenges([
                ...gameWithChallenge.challenges.filter(
                  ({ id }) => id !== challengeId,
                ),
                {
                  ...challengeToUpdate,
                  id: calculateNewChallengeId(challengeToUpdate.id),
                },
              ]),
            },
          ]
        : []),
    ];
  } else {
    return games;
  }
};

const reducer: React.Reducer<
  PdfData[],
  | { type: "storeGame"; game: PdfData }
  | { type: "deleteGame"; samsId: string }
  | { type: "addChallenge"; challenge: Challenge; samsId: string }
  | {
      type: "addChallengeCounter";
      samsId: string;
      team: TeamReference;
      set: number;
    }
  | { type: "deleteChallenge"; challengeId: string }
  | { type: "restoreChallenge"; challengeId: string }
  | { type: "updateLocation"; samsId: string; location: string }
  | { type: "updateGameNumber"; samsId: string; gameNumber: string }
  | { type: "updateScoresheetPdfUrl"; samsId: string; url: string }
  | { type: "updateYoutubeId"; samsId: string; youtubeId: string }
  | { type: "updateVideoOffset"; samsId: string; offset: number }
> = (state, action) => {
  let game: PdfData | undefined;
  switch (action.type) {
    case "storeGame":
      return [
        ...state.filter((game) => game.samsId !== action.game.samsId),
        action.game,
      ];
    case "deleteGame":
      game = state.find((game) => game.samsId === action.samsId);

      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game ? [{ ...game, samsId: `deleted-${game.samsId}` }] : []),
      ];
    case "updateLocation":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                gameData: {
                  ...game.gameData,
                  location: action.location,
                },
              },
            ]
          : []),
      ];
    case "updateGameNumber":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                gameData: {
                  ...game.gameData,
                  gameNumber: action.gameNumber,
                },
              },
            ]
          : []),
      ];
    case "updateScoresheetPdfUrl":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                gameData: {
                  ...game.gameData,
                  scoresheetPdfUrl: action.url,
                },
              },
            ]
          : []),
      ];
    case "updateVideoOffset":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                gameData: {
                  ...game.gameData,
                  gameStartVideoOffset: action.offset,
                },
              },
            ]
          : []),
      ];
    case "updateYoutubeId":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                gameData: {
                  ...game.gameData,
                  youtubeId: action.youtubeId,
                },
              },
            ]
          : []),
      ];
    case "addChallenge":
      game = state.find((game) => game.samsId === action.samsId);
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                challenges: sortChallenges([
                  ...game.challenges.filter(
                    (c) => c.id !== action.challenge.id,
                  ),
                  action.challenge,
                ]),
              },
            ]
          : []),
      ];
    case "addChallengeCounter":
      game = state.find((game) => game.samsId === action.samsId);
      const challengesUsedInSet = game?.challengesUsed?.[action.set] || {
        team1: 2,
        team2: 2,
      };
      return [
        ...state.filter((game) => game.samsId !== action.samsId),
        ...(game
          ? [
              {
                ...game,
                challengesUsed: {
                  ...game.challengesUsed,
                  [action.set]: {
                    ...challengesUsedInSet,
                    [action.team]:
                      challengesUsedInSet[action.team] <= 0
                        ? 2
                        : challengesUsedInSet[action.team] - 1,
                  },
                },
              },
            ]
          : []),
      ];
    case "deleteChallenge":
      return modifyChallengeId(
        state,
        action.challengeId,
        (id) => `deleted-${id}`,
      );
    case "restoreChallenge":
      return modifyChallengeId(state, action.challengeId, (id) =>
        id.replace("deleted-", ""),
      );
    default:
      return state;
  }
};

export const useGameDataContext = (): GameDataValues =>
  useContext(GameDataContext);

const GameDataContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const localStorage = getFromLocalStorage("games");
  const games =
    ((localStorage?.version || 0) >= VERSION && localStorage?.games) || [];
  const [gameState, dispatch] = useReducer(reducer, games);

  const [requestedGame, setRequestedGame] = useState<{
    samsId: string;
    scope: SamsScope;
  }>();

  const [gameLoadingState, setGameLoadingState] =
    useState<GameDataValues["gameLoadingState"]>("loading");
  const [game, setGame] = useState<PdfData>();

  useEffect(() => {
    putToLocalStorage("games", { games: gameState, version: VERSION });
    if (game) {
      const updatedGame = gameState.find((g) => g.samsId === game.samsId);
      setGame(updatedGame);
    }
  }, [gameState]);

  useEffect(() => {
    if (
      requestedGame?.samsId &&
      requestedGame.scope &&
      game?.samsId !== requestedGame.samsId
    ) {
      const game = gameState.find(
        (game) => game.samsId === requestedGame.samsId,
      );
      if (game) {
        setGameLoadingState("loaded");
        setGame(game);
      } else {
        setGameLoadingState("loading");
        loadGame(requestedGame);
      }
    }
  }, [requestedGame, game, gameState]);

  const loadGame = async ({
    samsId,
    scope,
  }: {
    samsId: string;
    scope: SamsScope;
  }) => {
    const data = await loadGameData({ samsId, scope });
    if (!data) {
      setGameLoadingState("error");
    } else {
      storeGame(data);
    }
  };

  const useGame = (samsId: string, scope: SamsScope) =>
    setRequestedGame({ samsId, scope });

  const storeGame = (game: PdfData) => dispatch({ type: "storeGame", game });

  const deleteGame = (samsId: string) =>
    dispatch({ type: "deleteGame", samsId });

  const deleteChallenge = (challengeId: string) =>
    dispatch({ type: "deleteChallenge", challengeId });

  const restoreChallenge = (challengeId: string) =>
    dispatch({ type: "restoreChallenge", challengeId });

  const addChallenge = (samsId: string, challenge: Challenge) =>
    dispatch({ type: "addChallenge", samsId, challenge });

  const addChallengeCounter: GameDataValues["addChallengeCounter"] = (
    team,
    set,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update location",
      );
    } else {
      dispatch({ type: "addChallengeCounter", samsId, team, set });
    }
  };

  const updateLocation: GameDataValues["updateLocation"] = (
    location,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update location",
      );
    } else {
      dispatch({ type: "updateLocation", location, samsId });
    }
  };

  const updateGameNumber: GameDataValues["updateGameNumber"] = (
    gameNumber,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update gameNumber",
      );
    } else {
      dispatch({ type: "updateGameNumber", gameNumber, samsId });
    }
  };

  const updateScoresheetPdfUrl: GameDataValues["updateScoresheetPdfUrl"] = (
    url,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update scoresheetPdfUrl",
      );
    } else {
      dispatch({ type: "updateScoresheetPdfUrl", url, samsId });
    }
  };

  const updateVideoOffset: GameDataValues["updateVideoOffset"] = (
    offset,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update videoOffset",
      );
    } else {
      dispatch({ type: "updateVideoOffset", offset, samsId });
    }
  };

  const updateYoutubeId: GameDataValues["updateYoutubeId"] = (
    youtubeId,
    samsId,
  ) => {
    if (!samsId) {
      samsId = game?.samsId;
    }
    if (!samsId) {
      console.error(
        "No samsId specified and no game loaded! Cannot update youtubeId",
      );
    } else {
      dispatch({ type: "updateYoutubeId", youtubeId, samsId });
    }
  };

  return (
    <GameDataContext.Provider
      value={{
        game,
        gameLoadingState,
        useGame,
        storeGame,
        deleteGame,
        addChallenge,
        addChallengeCounter,
        deleteChallenge,
        restoreChallenge,
        updateGameNumber,
        updateLocation,
        updateScoresheetPdfUrl,
        updateVideoOffset,
        updateYoutubeId,
        games: gameState,
      }}
    >
      {children}
    </GameDataContext.Provider>
  );
};

export default GameDataContextProvider;
